import { useUser, useClerk } from '@clerk/clerk-react'
import { useMutation, useQuery, useConvexAuth } from 'convex/react'
import { useEffect } from 'react'
import { useToast } from '../contexts/ToastContext'
import { api } from '../convex_generated/api'

export const useAuth = () => {
  const { user: clerkUser, isLoaded, isSignedIn } = useUser()
  const { openSignIn, openSignUp, signOut: clerkSignOut } = useClerk()
  const { showToast } = useToast()

  // isAuthenticated here means Convex *has* the token and has validated it.
  const { isLoading: convexAuthLoading, isAuthenticated: convexAuthenticated } = useConvexAuth()

  const upsertUser = useMutation(api.auth.upsertUser)

  // Always call useQuery with the same shape of args to keep hooks stable.
  // The query itself returns null when unauthenticated, which is harmless.
  const convexUser = useQuery(api.auth.getCurrentUser)

  // Upsert Convex user only after Convex has the auth token AND the query
  // has definitively returned null (no existing record).
  useEffect(() => {
    if (!convexAuthenticated) return
    if (convexUser === null) {
      upsertUser({
        username: clerkUser?.username ?? undefined,
        displayName: clerkUser?.fullName ?? undefined,
      }).catch((err: unknown) => {
        // A deleted account can't be revived by signing in again — say so and
        // end the session rather than leaving a signed-in shell with no profile.
        if (err instanceof Error && /deleted/i.test(err.message)) {
          showToast({ message: 'This account has been deleted.', type: 'error' })
          clerkSignOut().catch(console.error)
        } else {
          console.error(err)
        }
      })
    }
  }, [convexAuthenticated, convexUser, clerkUser?.fullName, clerkUser?.username, upsertUser, showToast, clerkSignOut])

  const signOut = async () => {
    await clerkSignOut()
    showToast({ message: 'You have been signed out', type: 'info' })
  }

  const hasRole = (role: string | string[]) => {
    if (!convexUser) return false
    if (convexUser.role === 'admin') return true
    if (Array.isArray(role)) return role.includes(convexUser.role)
    return convexUser.role === role
  }

  return {
    isLoaded: isLoaded && !convexAuthLoading,
    isAuthenticated: convexAuthenticated,
    isSignedIn: !!isSignedIn,
    user: convexUser ?? null,
    clerkUser,
    openSignIn,
    openSignUp,
    signOut,
    hasRole,
    canCreateContent: () => hasRole(['writer', 'artist', 'admin']),
    canModerate: () => hasRole(['admin']),
  }
}
